import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtClock, fmtPct, fmtTime, formatMoney, signClass, titleCase, useNow } from '../lib/format';
import type { PlanView } from '../lib/types';
import type { DeskTrade } from '../lib/types-setups';
import { HANDOVER_RIGHTS_LABEL } from '@vtf/shared';
import { Bar, Button, Card, CardHeader, cx, Empty, ErrorText, Input, PageHeader, Pill, Segmented, Stat } from '../components/ui';
import { OpenPnl } from '../components/PriceMark';
import type { TradeMark } from '../lib/types';

/** INT-PRG-05 / INT-DSH-02: who is responsible for each open trade right now, and trades in quiet hours with no cover. */
function OpenTrades({ trades, tz }: { trades: (DeskTrade & { mark?: TradeMark | null })[]; tz: string }) {
  const now = useNow(30_000);
  const uncovered = trades.filter((t) => t.uncoveredInQuietHours).length;
  return (
    <Card tone={uncovered ? 'down' : undefined}>
      <CardHeader
        title={
          <>
            Open trades <span className="font-normal text-muted">· {trades.length}</span>
            {!!uncovered && <span className="text-down"> · {uncovered} in quiet hours with no cover</span>}
          </>
        }
        right={<span className="text-xs text-muted">Responsible = who reports right now</span>}
      />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse">
          <thead>
            <tr className="text-left text-xs text-muted">
              {['Trade', 'Trader', 'Responsible now', 'Quiet hours', 'Open (delayed price)', 'Next report'].map((h) => (
                <th key={h} className={cx('px-4 py-2.5 font-medium', h.startsWith('Open') && 'text-right')}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => {
              const late = t.nextReport && (t.nextReport.status !== 'PENDING' || new Date(t.nextReport.dueAt).getTime() < now);
              return (
                <tr key={t.id} className={cx('border-t border-[#1a1f27]', t.uncoveredInQuietHours && 'bg-down-bg/40')}>
                  <td className="px-4 py-3">
                    <Link to={`/trades/${t.id}`} className="font-semibold">
                      {t.symbol} {t.direction === 'LONG' ? 'long' : 'short'}
                    </Link>
                    <span className="text-xs text-muted"> · {t.account}</span>
                  </td>
                  <td className="px-4 py-3 text-ink2">{t.owner.name}</td>
                  <td className="px-4 py-3">
                    {t.covered ? (
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold">{t.responsible.name}</span>
                        <Pill tone="live">covering{t.handover ? ` · ${HANDOVER_RIGHTS_LABEL[t.handover.rights].split(' (')[0]!.toLowerCase()}` : ''}</Pill>
                      </span>
                    ) : (
                      <span>
                        {t.responsible.name}
                        {t.handover && <span className="text-xs text-muted"> · cover arranged: {t.handover.cover.name}</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.uncoveredInQuietHours ? (
                      <Pill tone="down">No cover · quiet until {fmtTime(t.quiet!.until, tz)}</Pill>
                    ) : t.quiet?.inQuietHours ? (
                      <Pill>Quiet · covered</Pill>
                    ) : t.quiet ? (
                      <span className="text-xs text-muted">
                        {t.quiet.start}–{t.quiet.end} {t.quiet.tz}
                      </span>
                    ) : (
                      <span className="text-xs text-muted">none set</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <OpenPnl mark={t.mark} tz={tz} />
                  </td>
                  <td className={cx('px-4 py-3 font-mono text-sm', late ? 'text-warn' : 'text-ink2')}>{t.nextReport ? `#${t.nextReport.sequence} ${fmtTime(t.nextReport.dueAt, tz, Math.abs(new Date(t.nextReport.dueAt).getTime() - now) > 20 * 3600_000)}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!trades.length && <Empty>No open trades.</Empty>}
      </div>
    </Card>
  );
}

interface RiskResp {
  currency: string;
  currencies: string[];
  defaultCurrency: string;
  ratesAsOf: string | null;
  rates: { values: Record<string, string>; dates: Record<string, string> };
  kpis: { pendingApprovals: number; nextSlaSeconds: number | null; openRisk: string; todayPnl: string; equity: string; overdueReports: number };
  queue: { id: string; level: number; role: string; requestedAt: string; slaDueAt: string; slaSecondsLeft: number; canDecide: boolean; plan: PlanView }[];
  accounts: { id: string; code: string; market: string; baseCurrency: string; traders: { name: string }[]; equity: string | null; today: string | null; openRiskPct: string; maxOpenRiskPct: string; dailyLossUsedPct: string; dailyLossLimitPct: string; dailyLossWarning: boolean; locked: boolean; openTrades: number }[];
  attention: { kind: string; title: string; detail: string; at: string; tradeId: string | null }[];
  openTrades: (DeskTrade & { mark?: TradeMark | null })[];
}

function QueueRow({ q, onDone }: { q: RiskResp['queue'][number]; onDone: () => void }) {
  const now = useNow(1000);
  const [mode, setMode] = useState<'none' | 'reject' | 'change'>('none');
  const [reason, setReason] = useState('');
  const [size, setSize] = useState(q.plan.size);
  const decide = useMutation({ mutationFn: (body: object) => api(`/approvals/${q.id}/decision`, { body }), onSuccess: onDone });
  const left = (new Date(q.slaDueAt).getTime() - now) / 1000;
  const p = q.plan;
  const escalated = p.riskCheck?.checks.filter((c) => c.result === 'ESCALATE') ?? [];
  return (
    <div className="flex flex-col gap-3 border-t border-[#1a1f27] px-4 py-3.5 first:border-t-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-[220px] flex-col gap-1">
          <Link to={`/plans/${p.id}`} className="text-[15px] font-semibold text-ink">
            {p.instrument.symbol} {p.direction === 'LONG' ? 'long' : 'short'} <span className="text-sm font-normal text-muted">· level {q.level}</span>
          </Link>
          <span className="text-sub">
            {p.trader.name} · {p.account.code} · {fmtPct(p.riskPct)} risk · R:R {p.rewardRisk ?? '—'}
          </span>
        </div>
        <div className="flex min-w-[240px] flex-col gap-1">
          <span className="text-xs font-bold text-warn">ESCALATED · {escalated.map((c) => c.name.toLowerCase()).join(', ') || 'review'}</span>
          <span className="text-xs text-sub">{escalated[0]?.message}</span>
        </div>
        <div className="flex min-w-[90px] flex-col items-end">
          <span className={cx('font-mono text-base font-semibold', left < 120 ? 'text-down' : 'text-warn')}>{fmtClock(left)}</span>
          <span className="text-xs text-muted">SLA left</span>
        </div>
        {q.canDecide ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="success" onClick={() => decide.mutate({ decision: 'APPROVE' })} disabled={decide.isPending}>
              Approve
            </Button>
            <Button onClick={() => setMode(mode === 'change' ? 'none' : 'change')}>Approve with changes</Button>
            <Button variant="danger" onClick={() => setMode(mode === 'reject' ? 'none' : 'reject')}>
              Reject
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted">Not yours to decide</span>
        )}
      </div>
      {mode === 'change' && (
        <div className="flex flex-wrap gap-2">
          <Input mono aria-label="Approved size" value={size} onChange={(e) => setSize(e.target.value)} className="max-w-[160px]" />
          <Input aria-label="Reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="max-w-[360px]" />
          <Button variant="primary" onClick={() => decide.mutate({ decision: 'APPROVE_WITH_CHANGES', size, reason })} disabled={decide.isPending}>
            Approve size {size}
          </Button>
        </div>
      )}
      {mode === 'reject' && (
        <div className="flex flex-wrap gap-2">
          <Input aria-label="Reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="max-w-[420px]" />
          <Button variant="danger" onClick={() => decide.mutate({ decision: 'REJECT', reason })} disabled={decide.isPending}>
            Confirm reject
          </Button>
        </div>
      )}
      <ErrorText error={decide.error ? errorText(decide.error) : null} />
    </div>
  );
}

export function RiskDesk() {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  // null = the server's default: my reporting currency from Settings (D10)
  const [ccy, setCcy] = useState<string | null>(null);
  const { data, error } = useQuery({ queryKey: ['risk', ccy ?? 'default'], queryFn: () => api<RiskResp>(`/dashboards/risk${ccy ? `?currency=${ccy}` : ''}`), refetchInterval: 30_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['risk'] });
  const c = data?.currency ?? ccy ?? 'USD';
  const rateLine = data ? Object.entries(data.rates.values).map(([k, v]) => `${k} ${v}`).join(' · ') : '';
  const asOf = data?.ratesAsOf ? `at rates of ${new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${data.ratesAsOf}T00:00:00Z`))}` : 'no conversion needed';

  return (
    <>
      <PageHeader
        title="Risk desk"
        sub={
          data ? (
            <>
              {data.accounts.length} accounts · rates per 1 USD: {rateLine} ·{' '}
              <a href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer" className="text-xs">
                Rates By Exchange Rate API
              </a>
            </>
          ) : (
            'Loading…'
          )
        }
        right={data && <Segmented label="Show amounts in" options={data.currencies.map((x) => ({ value: x, label: x === data.defaultCurrency ? `${x} · mine` : x }))} value={data.currency} onChange={setCcy} />}
      />
      <ErrorText error={error ? errorText(error) : null} />
      {data && (
        <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Waiting for approval" value={data.kpis.pendingApprovals} tone={data.kpis.pendingApprovals ? 'text-warn' : ''} note={data.kpis.nextSlaSeconds != null ? `next SLA in ${fmtClock(data.kpis.nextSlaSeconds)}` : 'queue clear'} />
            <Stat label="Open risk, all accounts" value={formatMoney(data.kpis.openRisk, c)} note={`in ${c} ${asOf}`} />
            <Stat label="Floor P&L today" value={formatMoney(data.kpis.todayPnl, c, { signed: true })} tone={signClass(data.kpis.todayPnl)} note={`in ${c} ${asOf}`} />
            <Stat label="Floor equity" value={formatMoney(data.kpis.equity, c)} note={`in ${c} ${asOf}`} />
            <Stat label="Overdue reports" value={data.kpis.overdueReports} tone={data.kpis.overdueReports ? 'text-down' : ''} />
          </section>

          <Card tone={data.queue.length ? 'warn' : undefined}>
            <CardHeader title={<>Approval queue <span className="text-warn">· {data.queue.length} waiting</span></>} right={<span className="text-xs text-muted">Oldest first · unanswered after the SLA moves up a level</span>} />
            {data.queue.map((q) => (
              <QueueRow key={q.id} q={q} onDone={refresh} />
            ))}
            {!data.queue.length && <Empty>No plans waiting.</Empty>}
          </Card>

          <OpenTrades trades={data.openTrades} tz={tz} />

          <div className="flex flex-wrap items-start gap-3.5">
            <Card className="min-w-0 flex-[999_1_560px]">
              <CardHeader title={<>Accounts <span className="font-normal text-muted">in {c}</span></>} right={<span className="text-xs text-muted">{asOf}</span>} />
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse">
                  <thead>
                    <tr className="text-left text-xs text-muted">
                      {['Account', 'Trader', 'Equity', 'Today', 'Open risk', 'Daily loss'].map((h) => (
                        <th key={h} className={cx('px-4 py-2.5 font-medium', (h === 'Equity' || h === 'Today') && 'text-right')}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((a) => (
                      <tr key={a.id} className="border-t border-[#1a1f27]">
                        <td className="px-4 py-3">
                          <span className="font-semibold">{a.code}</span>
                          <br />
                          <span className="text-xs text-muted">
                            {a.market} · base {a.baseCurrency}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-ink2">{a.traders.map((t) => t.name).join(', ')}</td>
                        <td className="px-4 py-3 text-right font-mono">{a.equity ? formatMoney(a.equity, c) : 'no rate'}</td>
                        <td className={cx('px-4 py-3 text-right font-mono', signClass(a.today))}>{a.today ? formatMoney(a.today, c, { signed: true }) : '—'}</td>
                        <td className="min-w-[140px] px-4 py-3">
                          <div className="mb-1 text-xs text-sub">
                            {fmtPct(a.openRiskPct)} / {fmtPct(a.maxOpenRiskPct, 1)}
                          </div>
                          <Bar pct={(Number(a.openRiskPct) / Number(a.maxOpenRiskPct)) * 100} />
                        </td>
                        <td className="min-w-[160px] px-4 py-3">
                          <div className={cx('mb-1 flex justify-between text-xs', a.locked ? 'text-down' : a.dailyLossWarning ? 'text-warn' : 'text-sub')}>
                            <span>
                              {fmtPct(a.dailyLossUsedPct)} / {fmtPct(a.dailyLossLimitPct, 1)}
                            </span>
                            <span>{a.locked ? 'locked' : a.dailyLossWarning ? 'warning' : ''}</span>
                          </div>
                          <Bar pct={(Number(a.dailyLossUsedPct) / Number(a.dailyLossLimitPct)) * 100} color={a.locked ? '#ff7a6e' : '#f5b544'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card className="flex min-w-0 flex-[1_1_300px] flex-col gap-2.5 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Needs attention</h2>
              {!data.attention.length && <span className="text-sub">Nothing right now.</span>}
              {data.attention.map((a, i) => (
                <div key={i} className="flex flex-col gap-1 rounded-lg bg-raised px-3 py-2.5">
                  <Pill tone={a.kind.includes('MISSED') || a.kind.includes('LOCKED') || a.kind.includes('UNCOVERED') ? 'down' : 'warn'} className="self-start">
                    {titleCase(a.kind)}
                  </Pill>
                  {a.tradeId ? (
                    <Link to={`/trades/${a.tradeId}`} className="font-semibold text-ink">
                      {a.title}
                    </Link>
                  ) : (
                    <span className="font-semibold">{a.title}</span>
                  )}
                  <span className="text-xs text-sub">{a.detail.replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z/, (iso) => fmtTime(iso, tz, true))}</span>
                </div>
              ))}
            </Card>
          </div>
        </>
      )}
    </>
  );
}
